import React, { useEffect, useRef, useState } from 'react';

import { getRem } from 'lib/core';
import { LIST_SIZES } from 'lib/list';
import { Menu, MenuListItem } from 'lib/menu';
import { useShareForwardedRef } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CHIP_BACKGROUND_APPEARANCES } from '../../constants';
import { Chip } from '../Chip/Chip';

const StyledChip = styled(Chip)`
  max-width: ${getRem('256px')};
`;

const MultiSelectChip = React.forwardRef(
  (
    {
      backgroundAppearance = CHIP_BACKGROUND_APPEARANCES.LIGHT,
      className = '',
      dataTestId = undefined,
      hidePrefixOnSelect = false,
      hideSelectAll = false,
      icon = undefined,
      menuProps = {},
      onClose = () => {},
      onOpen = () => {},
      onSelect,
      options,
      prefix,
      selectedValues,
      size = LIST_SIZES.STANDARD,
      ...other
    },
    ref
  ) => {
    const menuContainerRef = useRef();
    const targetRef = useShareForwardedRef(ref);

    const [isOpen, setIsOpen] = useState(false);

    const isAllSelected = selectedValues.length === options.length;
    const isAnySelected = selectedValues.length > 0;
    const isIndeterminate = !isAllSelected && isAnySelected;

    const handleOpen = () => {
      setIsOpen(true);
      onOpen();
    };

    const handleClose = () => {
      setIsOpen(false);
      onClose();
    };

    const handleRemove = () => {
      onSelect([]);
    };

    const handleSelect = (option) => {
      const isSelected = selectedValues.includes(option.value);

      if (isSelected) {
        const newSelectedValues = selectedValues.filter((value) => value !== option.value);
        onSelect(newSelectedValues);
      } else {
        const newSelectedValues = [...selectedValues, option.value];
        onSelect(newSelectedValues);
      }
    };

    const handleAllSelect = () => {
      if (!isAnySelected) {
        const optionsExcludingDisabled = options.filter((option) => !option.isDisabled);
        const justTheValuesOfTheOptionsExcludingDisabled = optionsExcludingDisabled.map((opt) => opt.value);
        onSelect(justTheValuesOfTheOptionsExcludingDisabled);
        return;
      }
      onSelect([]);
    };

    const prefixToDisplay = hidePrefixOnSelect ? '' : prefix;
    const selectedValuesLength = selectedValues.length;
    const displayLabel =
      selectedValuesLength > 0 ? options.find((option) => option.value === selectedValues[0]).label : null;

    const getIsOptionSelected = (option) => {
      return selectedValues.some((opt) => opt === option.value);
    };

    const getLabel = (selectedValuesLength) => {
      if (selectedValuesLength === 0) {
        return prefix;
      }
      return hidePrefixOnSelect ? (
        displayLabel
      ) : (
        <>
          {prefixToDisplay}: {displayLabel}
        </>
      );
    };

    const label = getLabel(selectedValuesLength);
    const labelSuffix = `+ ${selectedValuesLength - 1}`;

    useEffect(() => {
      const setIsOpenFromMenu = (event) => {
        if (event.detail.ref === menuContainerRef) {
          if (event.detail.openMenu === false) {
            handleClose();
          } else {
            handleOpen();
          }
        }
      };
      document.addEventListener('customMenuOpenEvent', setIsOpenFromMenu);
      return () => document.removeEventListener('customMenuOpenEvent', setIsOpenFromMenu);
    }, []);

    return (
      <>
        <StyledChip
          className={className ? `${className} ${className}-chip` : undefined}
          hasDropdown={selectedValuesLength === 0}
          isSelected={selectedValuesLength > 0}
          labelSuffix={selectedValuesLength > 1 ? labelSuffix : null}
          onClick={isOpen ? handleClose : handleOpen}
          onRemove={selectedValuesLength === 0 ? undefined : handleRemove}
          {...{ backgroundAppearance, dataTestId, icon, isOpen, label }}
          {...other}
          ref={targetRef}
        />
        <Menu
          dataTestId={dataTestId ? `${dataTestId}-menu-container` : undefined}
          isOpen={isOpen}
          onClose={handleClose}
          onOpen={handleOpen}
          {...menuProps}
          dimensionRef={targetRef}
          labelRef={targetRef}
          multi
          ref={menuContainerRef}
        >
          {!hideSelectAll ? (
            <MenuListItem
              dataTestId={dataTestId ? `${dataTestId}-select-all` : undefined}
              onClick={handleAllSelect}
              isSelected={isAllSelected || isIndeterminate}
              indeterminate={isIndeterminate}
              key="select-all"
              showDivider
              hideSelectedItemCheckmark={false}
            >
              Select All
            </MenuListItem>
          ) : (
            <React.Fragment key="select-all" />
          )}
          {options.map((option, ind) => {
            return (
              <MenuListItem
                dataTestId={dataTestId ? `${dataTestId}-menu-item-${ind}-label` : undefined}
                isDisabled={option.isDisabled}
                isSelected={getIsOptionSelected(option)}
                key={ind}
                onClick={() => handleSelect(option)}
                showDivider={option.showDivider}
              >
                {option.label}
              </MenuListItem>
            );
          })}
        </Menu>
      </>
    );
  }
);

MultiSelectChip.propTypes = {
  /** @ignore */
  backgroundAppearance: PropTypes.oneOf(Object.values(CHIP_BACKGROUND_APPEARANCES)),
  /** Adds additional className prop to modify components style */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Hide the Select All option in chip dropdown */
  hideSelectAll: PropTypes.bool,
  /** Hide the prefix text when one or more options is selected */
  hidePrefixOnSelect: PropTypes.bool,
  /** Shows icon inside the chip. Use icon component from the library */
  icon: PropTypes.node,
  /** If true, changes visual appearance to indicate that element is non-interactive */
  isDisabled: PropTypes.bool,
  /** Menu props you wish to override or pass through to the Menu component (See Menu for full list) */
  menuProps: PropTypes.shape({}),
  /** Callback that is called when select is being closed */
  onClose: PropTypes.func,
  /** Callback that is called when select is getting opened */
  onOpen: PropTypes.func,
  /** Callback that is called when an option is selected (most likely a `handleSelect` function to manage selectedOptions state) */
  onSelect: PropTypes.func.isRequired,
  /** Array of options */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /** Option item icon */
      icon: PropTypes.node,
      /** Option item label */
      label: PropTypes.node.isRequired,
      /** Option item value */
      value: PropTypes.node.isRequired,
    })
  ).isRequired,
  /** Prefix for chip label when an option is selected and acts as a chip label when no option is selected */
  prefix: PropTypes.node.isRequired,
  /** Array of just the values of selected option(s) */
  selectedValues: PropTypes.node.isRequired,
  /** Options size */
  size: PropTypes.oneOf(Object.values(LIST_SIZES)),
};

export { MultiSelectChip };
